import {
  ExperimentOutlined,
  LoadingOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import {
  Button,
  Form,
  Input,
  Popconfirm,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'
import VSpace from '../../../common/VSpace'
import ChatSessionModal from '../../Chats/ChatSessionModal'
import {
  describeMutationJob,
  emptyMutantsMessage,
  getDefaultGoldenPromptFilters,
  GoldenPrompt,
  GoldenPromptFilters,
  MutationJobTone,
  PromptTypeFilter,
} from './goldenPromptsAdminModel'
import {
  useDeleteGoldenPromptAdminMutation,
  useEnqueueMutationAdminMutation,
  useGoldenPromptsAdminListQuery,
  useMutationStatusAdminQuery,
} from './goldenPromptsAdminQueries'
import GoldenPromptFormModal from './GoldenPromptFormModal'
import GoldenPromptUploadModal from './GoldenPromptUploadModal'

type FormValues = {
  userId?: string
  promptType: PromptTypeFilter
}

const formValuesToFilters = (
  values: FormValues,
  page: number,
  pageSize: number,
): GoldenPromptFilters => ({
  userId: values.userId || undefined,
  promptType: values.promptType ?? 'any',
  parentsOnly: true,
  page,
  pageSize,
})

const TONE_COLOR: Record<MutationJobTone, string> = {
  idle: 'default',
  progress: 'processing',
  success: 'green',
  error: 'red',
}

type MutationStatusCellProps = {
  promptId: string
}

const MutationStatusCell = ({ promptId }: MutationStatusCellProps) => {
  const { data, isLoading, isError } = useMutationStatusAdminQuery(promptId)
  if (isLoading) {
    return <Spin size="small" indicator={<LoadingOutlined spin />} />
  }
  if (isError) {
    return (
      <Tooltip title="Failed to load mutation status">
        <Tag color="default" style={{ marginRight: 0 }}>Unavailable</Tag>
      </Tooltip>
    )
  }
  const description = describeMutationJob(
    data?.job ?? null,
    data?.mutants.length ?? 0,
  )
  const lastError = data?.job?.last_error ?? null
  const tag = (
    <Tag color={TONE_COLOR[description.tone]} style={{ marginRight: 0 }}>
      {description.label}
    </Tag>
  )
  if (description.tone === 'error' && lastError) {
    return <Tooltip title={lastError}>{tag}</Tooltip>
  }
  return tag
}

type ExpandedMutantsPanelProps = {
  parent: GoldenPrompt
  onEdit: (row: GoldenPrompt) => void
  onDelete: (promptId: string) => void
}

const ExpandedMutantsPanel = ({
  parent,
  onEdit,
  onDelete,
}: ExpandedMutantsPanelProps) => {
  const { data, isLoading } = useMutationStatusAdminQuery(parent.prompt_id)
  const mutants = data?.mutants ?? []

  if (isLoading) {
    return <Spin />
  }
  if (mutants.length === 0) {
    return (
      <Typography.Text type="secondary">
        {emptyMutantsMessage(data?.job ?? null)}
      </Typography.Text>
    )
  }

  const columns: ColumnsType<GoldenPrompt> = [
    {
      title: 'Variant',
      dataIndex: 'prompt_text',
      ellipsis: true,
      render: (val: string) => (
        <Typography.Text ellipsis style={{ maxWidth: 520 }}>
          {val}
        </Typography.Text>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'prompt_type',
      width: 120,
      render: (val: 'normal' | 'adversarial') => (
        <Tag color={val === 'adversarial' ? 'volcano' : 'blue'}>{val}</Tag>
      ),
    },
    {
      title: 'Added',
      dataIndex: 'datetime_added',
      width: 170,
      render: (val: string) => dayjs(val).format('MMM D, YYYY h:mm A'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_: unknown, row: GoldenPrompt) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Button type="link" size="small" onClick={() => onEdit(row)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this variant?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(row.prompt_id)}
          >
            <Button type="link" size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ]

  return (
    <Table<GoldenPrompt>
      dataSource={mutants}
      rowKey="prompt_id"
      columns={columns}
      pagination={false}
      size="small"
      onRow={(record) => ({
        onClick: () => onEdit(record),
        style: { cursor: 'pointer' },
      })}
    />
  )
}

const GoldenPromptsExplorer = () => {
  const [form] = Form.useForm<FormValues>()
  const [filters, setFilters] = useState<GoldenPromptFilters>(() => getDefaultGoldenPromptFilters())
  const [modalOpen, setModalOpen] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [editing, setEditing] = useState<GoldenPrompt | null>(null)
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const { data, isLoading } = useGoldenPromptsAdminListQuery(filters)
  const deleteMutation = useDeleteGoldenPromptAdminMutation()
  const enqueueMutation = useEnqueueMutationAdminMutation()

  const handleApply = () => {
    const values = form.getFieldsValue()
    setFilters(formValuesToFilters(values, 1, filters.pageSize))
  }

  const handleReset = () => {
    form.resetFields()
    setFilters(getDefaultGoldenPromptFilters())
  }

  const openCreate = () => {
    setEditing(null)
    setModalOpen(true)
  }

  const openEdit = (row: GoldenPrompt) => {
    setEditing(row)
    setModalOpen(true)
  }

  const columns: ColumnsType<GoldenPrompt> = [
    {
      title: 'Prompt',
      dataIndex: 'prompt_text',
      ellipsis: true,
      render: (val: string) => (
        <Typography.Text ellipsis style={{ maxWidth: 380 }}>
          {val}
        </Typography.Text>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'prompt_type',
      width: 130,
      render: (val: 'normal' | 'adversarial') => (
        <Tag color={val === 'adversarial' ? 'volcano' : 'blue'}>{val}</Tag>
      ),
    },
    {
      title: 'Variants',
      key: 'variants',
      width: 140,
      render: (_: unknown, row: GoldenPrompt) => (
        <MutationStatusCell promptId={row.prompt_id} />
      ),
    },
    {
      title: 'Author',
      dataIndex: 'user_id',
      width: 160,
      render: (val: string) => (
        <Typography.Text
          copyable={{ text: val }}
          style={{ fontFamily: 'monospace', fontSize: 12 }}
        >
          {val.slice(0, 8)}…
        </Typography.Text>
      ),
    },
    {
      title: 'Session',
      dataIndex: 'session_ref',
      width: 140,
      render: (val: string | null) =>
        val ? (
          <Typography.Link
            onClick={(e) => {
              e.stopPropagation()
              setSelectedSessionId(val)
            }}
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          >
            {val.slice(0, 8)}…
          </Typography.Link>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Added',
      dataIndex: 'datetime_added',
      width: 170,
      render: (val: string) => dayjs(val).format('MMM D, YYYY h:mm A'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_: unknown, row: GoldenPrompt) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Generate adversarial variants">
            <Button
              type="link"
              size="small"
              icon={<ExperimentOutlined />}
              loading={
                enqueueMutation.isLoading &&
                enqueueMutation.variables === row.prompt_id
              }
              onClick={() => enqueueMutation.mutate(row.prompt_id)}
            >
              Mutate
            </Button>
          </Tooltip>
          <Button type="link" size="small" onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this prompt and all its variants?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteMutation.mutate(row.prompt_id)}
          >
            <Button type="link" size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ]

  return (
    <VSpace>
      <Form form={form} initialValues={{ promptType: 'any' }} onFinish={handleApply}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Form.Item name="promptType" style={{ marginBottom: 0 }}>
            <Select
              style={{ width: 160 }}
              options={[
                { value: 'any', label: 'Any type' },
                { value: 'normal', label: 'Normal' },
                { value: 'adversarial', label: 'Adversarial' },
              ]}
            />
          </Form.Item>
          <Form.Item name="userId" style={{ marginBottom: 0 }}>
            <Input placeholder="Author user ID" allowClear style={{ width: 240 }} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit">Apply</Button>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button onClick={handleReset}>Reset</Button>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginLeft: 'auto' }}>
            <Button icon={<UploadOutlined />} onClick={() => setUploadModalOpen(true)}>
              Upload CSV
            </Button>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Golden Prompt
            </Button>
          </Form.Item>
        </div>
      </Form>
      <Table<GoldenPrompt>
        dataSource={data?.items}
        loading={isLoading}
        rowKey="prompt_id"
        columns={columns}
        expandable={{
          expandedRowRender: (record) => (
            <ExpandedMutantsPanel
              parent={record}
              onEdit={openEdit}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ),
        }}
        onRow={(record) => ({
          onClick: () => openEdit(record),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: filters.page,
          pageSize: filters.pageSize,
          total: data?.total,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50],
          showTotal: (total) => `${total} golden prompts`,
          onChange: (page, pageSize) =>
            setFilters((prev) => ({ ...prev, page, pageSize })),
        }}
      />
      <GoldenPromptFormModal
        open={modalOpen}
        editing={editing}
        onClose={() => setModalOpen(false)}
      />
      {uploadModalOpen && (
        <GoldenPromptUploadModal
          open
          onClose={() => setUploadModalOpen(false)}
        />
      )}
      <ChatSessionModal
        sessionId={selectedSessionId}
        onClose={() => setSelectedSessionId(null)}
      />
    </VSpace>
  )
}

export default GoldenPromptsExplorer
